import React from 'react';
import { Breakpoint } from '@noths/polaris-client-ribbons-base';
import { Picture, PictureSource } from '@noths/polaris-client-ribbons-design-system';

import * as styles from './HeroCarouselItem.styles';

export interface HeroCarouselItemProps {
  alt: string;
  headingLine1: string;
  headingLine2?: string;
  headingPosition: 'top' | 'bottom';
  // Desktop image, cropped wide (3:1), used at/above the L breakpoint.
  image: string;
  // Mobile image, cropped tall (3:4), used below the L breakpoint. Falls back to `image` if omitted.
  imageMobile?: string;
  priority?: boolean;
}

/**
 * The sliding part of a hero slide: the image with its heading overlay. The paragraph, T&Cs and
 * CTA are rendered separately by `HeroCarouselCaption` so they can swap in place rather than slide.
 *
 * Uses the design-system `Picture`: `PictureSource` applies a `min-width` query, so the wide (3:1)
 * desktop crop sits in a `min-width: L` source and the tall (3:4) mobile crop is the default `src`.
 * The first slide loads eagerly via `lazyLoading={false}`.
 */
export const HeroCarouselItem = ({
  alt,
  headingLine1,
  headingLine2,
  headingPosition,
  image,
  imageMobile,
  priority = false,
}: HeroCarouselItemProps) => (
  <div css={styles.imageWrapper}>
    <Picture
      alt={alt}
      fetchPriority={priority ? 'high' : undefined}
      imgCss={styles.image}
      lazyLoading={!priority}
      src={imageMobile ?? image}
    >
      <PictureSource minBreakpoint={Breakpoint.L} srcSet={image} />
    </Picture>
    <h2
      css={[styles.heading, headingPosition === 'top' ? styles.headingTop : styles.headingBottom]}
    >
      <span>{headingLine1}</span>
      {headingLine2 && <span>{headingLine2}</span>}
    </h2>
  </div>
);
